import { memo, useCallback, useMemo } from 'react';
import { mergeClasses } from '@fluentui/react-components';
import { Icon } from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import { useInitialData } from '../../../hooks/useInitialData';
import { isWorkDay } from '../utils/isWorkDay';
import { useTimeGridStyles } from '../useTimeGridStyles';
import { todayDate } from '../hooks/useDateStyles';
import { useGridStore, useAreAllProjectsExpanded } from '../state';

type HeaderRowProps = {
    dates: string[];
};

export const HeaderRowComponent = memo(function HeaderRowComponent({ dates }: HeaderRowProps) {
    const { i18n } = useTranslation();
    const {
        data: {
            configuration: { workWeek, showAllocations },
        },
    } = useInitialData();
    const styles = useTimeGridStyles();
    const allExpanded = useAreAllProjectsExpanded();
    const expandAll = useGridStore(s => s.expandAllProjects);
    const collapseAll = useGridStore(s => s.collapseAllProjects);

    const handleToggleAll = useCallback(() => {
        if (allExpanded) {
            collapseAll();
        } else {
            expandAll();
        }
    }, [allExpanded, expandAll, collapseAll]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggleAll();
            }
        },
        [handleToggleAll],
    );

    const resolvedWorkWeek = useMemo(() => workWeek ?? [], [workWeek]);

    const dateLabels = useMemo(() => {
        const checkWorkDay = isWorkDay(resolvedWorkWeek);
        const locale = i18n.language;

        return dates.map(date => {
            const d = new Date(date);
            return {
                date,
                dayName: d.toLocaleDateString(locale, { weekday: 'long' }),
                dateLabel: d.toLocaleDateString(locale, { month: 'short', day: 'numeric' }),
                isToday: date.slice(0, 10) === todayDate,
                isNonWorkday: !checkWorkDay(date),
            };
        });
    }, [dates, resolvedWorkWeek, i18n.language]);

    return (
        <div className={mergeClasses(styles.header, showAllocations && styles.headerWithAllocation)} role="row">
            <div
                className={mergeClasses(styles.headerCell, styles.stickyHeaderCol1)}
                role="columnheader"
                onClick={handleToggleAll}
                onKeyDown={handleKeyDown}
                tabIndex={0}
                style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'flex-start', columnGap: '8px' }}
                aria-label={`All Tasks, ${allExpanded ? 'collapse' : 'expand'} all projects`}
            >
                <Icon iconName="ChevronRight" className={mergeClasses(styles.expandIcon, allExpanded && styles.expandIconExpanded)} aria-hidden="true" />
                All Tasks
            </div>

            <div className={mergeClasses(styles.headerCell, styles.stickyHeaderCol2)} role="columnheader">
                Work Type
            </div>

            {dateLabels.map(({ date, dayName, dateLabel, isToday, isNonWorkday }) => (
                <div
                    key={date}
                    className={mergeClasses(styles.headerCell, isNonWorkday && styles.headerCellNonWorkday, isToday && styles.headerCellToday)}
                    role="columnheader"
                    data-non-workday={isNonWorkday || undefined}
                >
                    <div>{dayName}</div>
                    <div className={styles.headerCellDateSub}>{dateLabel}</div>
                </div>
            ))}

            <div className={mergeClasses(styles.headerCell, styles.headerCellTotal)} role="columnheader">
                Period Total
            </div>

            {showAllocations && (
                <div className={mergeClasses(styles.headerCell, styles.headerCellTotal)} role="columnheader">
                    Allocation
                </div>
            )}
        </div>
    );
});
